import exclamationBlueIcon from "@assets/images/exclamation-blue.svg";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent
} from "@material-ui/core";
import AdvancedTable from "@saleor/apps/components/AdvancedTable/AdvancedTable";
import MyTypography from "@saleor/components/MyTypography";
import { getItemByNo } from "@saleor/createOrChangeNotRefContract/helpers";
import useSortPriceCalc from "@saleor/createOrChangeNotRefContract/hooks/useSortPriceCal";
import { NotRefOrderQueryParams } from "@saleor/createOrChangeNotRefContract/urls";
import { orderPriceLinesMapping } from "@saleor/createOrChangeNotRefContract/utils";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";
import usePriceNotFoundData from "./usePriceNotFoundData";
interface DialogConfirmDeleteProps {
  onCancel: () => void;
  onConfirm: (value) => void;
  open: boolean;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  okText?: string | React.ReactNode;
  resultData?: any;
  confirmPopup?: boolean;
  formik: FormikProps<any>;
  orderId?: string;
  params: NotRefOrderQueryParams;
}

const PriceNotFoundPopup: React.FC<DialogConfirmDeleteProps> = ({
  onCancel,
  onConfirm,
  open,
  okText,
  resultData,
  formik,
  params
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const intl = useIntl();
  const classes = useStyles({});

  const sortedOrderItems = resultData?.getPriceCalculation?.response?.orderItemsOut?.sort(
    (itemA, itemB) => Number(itemA.itemNo) - Number(itemB.itemNo)
  );
  const orderItems = sortedOrderItems || [];

  const priceModalSchema = Yup.object().shape({
    lines: Yup.array().of(
      Yup.object().shape({
        pricePerUnit: Yup.number().test(
          "nonEmptypricePerUnit",
          " ",
          (value, { parent }) => {
            if (parent?.materialType === "85" && parent?.manualPriceFlag) {
              return !!value && parseFloat(value.toString()) !== 0;
            }
            return true;
          }
        )
      })
    )
  });

  const itemCondition = (item, lines) =>
    (item?.manualPriceFlag &&
      !+getItemByNo(lines, item?.itemNo)?.pricePerUnit) ||
    (item?.materialType !== "85" &&
      item?.priceStatus === "E" &&
      item?.priceStatusDesc === "ไม่มีราคา");

  const lines = [...(formik?.values?.lines ?? [])];
  if (!params.asc) {
    lines.reverse();
  }
  const filteredItems = orderItems?.filter(
    item => item?.itemNo && itemCondition(item, lines)
  );
  const formikPriceDetails = useFormik({
    initialValues: {
      lines: []
    },
    enableReinitialize: false,
    validateOnMount: true,
    validateOnChange: true,
    validationSchema: priceModalSchema,
    onSubmit: () => {
      //
    }
  });

  useEffect(() => {
    if (formikPriceDetails.values.lines.length < 1) {
      const lines = orderPriceLinesMapping(filteredItems);
      formikPriceDetails.setValues({ lines });
      if (lines.length === 0) {
        onCancel();
      }
    }
  }, [formikPriceDetails.values.lines.length]);

  const validate = async formikPrice => {
    const validation = await formikPrice?.validateForm();
    return validation && Object.keys(validation).length;
  };

  const handleConfirm = async formikPrice => {
    if (await validate(formikPrice)) {
      setIsSubmitted(false);
    } else {
      onConfirm(formikPrice?.values);
      setIsSubmitted(true);
    }
  };

  useEffect(() => {
    if (formikPriceDetails.dirty && !isSubmitted) {
      setIsSubmitted(true);
    }
  }, [formikPriceDetails?.dirty]);

  const itemsWithoutPriceAfterMap = formikPriceDetails?.values?.lines?.map(
    (item, index) => ({
      ...item,
      indexNumber: index
    })
  );

  const { columnsList } = usePriceNotFoundData({ ...formikPriceDetails });
  const { handleSort, sortDirection } = useSortPriceCalc({
    ...formikPriceDetails
  });

  return (
    formikPriceDetails?.values?.lines?.length >= 1 &&
    open && (
      <Dialog open={open} className={classes.root}>
        <Box className={classes.dialogHeader}>
          <img src={exclamationBlueIcon} alt="" />
          <MyTypography variant="h6" className={classes.title}>
            พบสินค้าไม่มีข้อมูลราคาในระบบ SAP
          </MyTypography>
          <div className={classes.textError}>โปรดระบุ ราคาต่อหน่วย</div>
        </Box>
        <DialogContent>
          <div className={classes.root}>
            <AdvancedTable
              reRenderKey="itemNo"
              rootClassName={classes.MarginTop}
              cellAlign="center"
              columnsList={columnsList}
              rowsData={itemsWithoutPriceAfterMap}
              onRowClick={null}
              borderRequired={false}
              handleSort={() => {
                handleSort();
              }}
              sortDirection={new Map([["itemNo", sortDirection]])}
              errors={formikPriceDetails?.errors?.lines}
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogBottom} disableSpacing={true}>
          <Button
            id="backButton"
            variant={"outlined"}
            color={"secondary"}
            className={classes.btnAction}
            onClick={onCancel}
          >
            <MyTypography variant={"body2"}>
              {intl.formatMessage({ defaultMessage: "กลับไป" })}
            </MyTypography>
          </Button>
          <Button
            id="confirmPriceButton"
            color={"secondary"}
            variant={"contained"}
            type={"submit"}
            className={classes.btnAction}
            fullWidth={true}
            onClick={() => {
              handleConfirm(formikPriceDetails);
            }}
          >
            {okText ? okText : <FormattedMessage defaultMessage={"ยืนยัน"} />}
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

export default PriceNotFoundPopup;
